import { getPrebidInstances } from "./adRequestKPV";
import { log, shouldLogByProbability } from "./logs";

const UNKNOWN = "unknown"; // fallback value for undefined event data

export class ImpressionsCounter {
  private static instance: ImpressionsCounter | null = null;

  private impressionsCount: number = 0;
  private trackingStarted: boolean = false;
  private trackedSlots = new Set<string>();
  private isGAM: boolean;

  constructor() {
    this.detectGAM();
    // start tracking impressions immediately as a background process once instance created
    this.startTracking();
  }

  public static getInstance(): ImpressionsCounter {
    if (!ImpressionsCounter.instance) {
      ImpressionsCounter.instance = new ImpressionsCounter();
    }
    return ImpressionsCounter.instance;
  }

  private detectGAM() {
    this.isGAM =
      !!window.googletag &&
      (!!window.googletag.pubads || !!window.googletag.cmd);
  }

  /**
   * Starts tracking ad impressions based on the available ad serving method.
   * This method is triggered automatically when the class is initialized.
   *
   * - If Google Ad Manager (GAM) is available - Use GPT (`slotRenderEnded`) to count impressions.
   * - If GAM is not available - Use Prebid (`bidWon`) as a fallback to count impressions.
   * - Ensures tracking starts only once per instance to prevent duplicate event listeners.
   *
   * **Avoid double-counting:**
   *
   * If an impression comes from Prebid and is rendered via GAM,
   * both `slotRenderEnded` (GPT) and `bidWon` (Prebid) could fire for the same ad.
   * That mean GPT already includes Prebid impressions if Prebid is integrated with GAM.
   *
   */
  private startTracking() {
    if (this.trackingStarted) return;
    this.trackingStarted = true;

    try {
      if (this.isGAM) {
        this.trackGptImpressions();
      } else {
        this.trackPrebidImpressions();
      }
    } catch (error) {
      if (shouldLogByProbability()) {
        log(
          "rlink.impressionTracking",
          {
            error: error instanceof Error ? error.message : error,
          },
          { severity: 3 },
        );
      }
    }
  }

  private trackPrebidImpressions() {
    const prebidInstances = getPrebidInstances();
    if (prebidInstances.length === 0) return;

    prebidInstances.forEach((pbjs) => {
      pbjs.onEvent("bidWon", (event) => {
        const adUnitCode = event.adUnitCode || `${UNKNOWN}-slot`;
        const creativeId = event.creativeId || UNKNOWN;
        const bidder = event.bidder || UNKNOWN;
        const slotKey = `${adUnitCode}_${bidder}_${creativeId}`;

        // prevent counting duplicated prebid impressions
        if (this.trackedSlots.has(slotKey)) {
          return;
        }
        this.impressionsCount++;
        this.trackedSlots.add(slotKey);
      });
    });
  }

  private trackGptImpressions() {
    window.googletag?.cmd?.push(() => {
      window.googletag.pubads().addEventListener("slotRenderEnded", (event) => {
        const slotId = event.slot.getSlotElementId();
        const creativeId = event.sourceAgnosticCreativeId || UNKNOWN; // unique identifier for ad content

        const isValidImpression = !event.isEmpty && event.slotContentChanged;
        const slotKey = `${slotId}_${creativeId}`;

        // avoid to count impression when:
        // - the same creative being rendered for the same slot
        // - empty ad slot occurs
        // - slot content wasn't changed
        if (this.trackedSlots.has(slotKey) || !isValidImpression) {
          return;
        }

        this.impressionsCount++; // count unique impression
        this.trackedSlots.add(slotKey);
      });
    });
  }

  public getImpressionsCount(): number {
    return this.impressionsCount;
  }
  public resetCounter() {
    this.impressionsCount = 0;
    this.trackingStarted = false; // Allow tracking to restart if needed
  }
}
